/**
 * Locale-aware date/time helpers for {@link DatetimeField}.
 *
 * Everything visual the picker shows — the trigger's display string, whether
 * the clock is 12- or 24-hour, the weekday header order, the weekday/month
 * names — is derived from `Intl` for a resolved BCP-47 locale, so we never hand-
 * maintain per-language format strings or translation keys for calendar labels.
 *
 * The locale comes from {@link resolveLocale}: the browser's preferred locale in
 * the UI language (its region decides date order / week start / 12-vs-24h), else
 * a representative locale for that language. Any locale `Intl` knows (incl.
 * ISO-style YYYY-MM-DD East-Asian locales) formats correctly for free.
 */

import { resolveLocale } from '../../../i18n/date-format';

/** JS `Date` weekday numbering: 0 = Sunday … 6 = Saturday. */
export type WeekdayIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface DateTimeFormat {
  /** Resolved BCP-47 locale driving all `Intl` formatting below. */
  readonly locale: string;
  /** True when the locale uses a 12-hour clock — the picker adds an AM/PM column. */
  readonly hour12: boolean;
  /** First day of the week in JS `Date` numbering (0 = Sunday … 6 = Saturday). */
  readonly firstDayOfWeek: WeekdayIndex;
  /** Short weekday labels, ordered starting at {@link firstDayOfWeek}. */
  readonly weekdayLabels: string[];
  /** Localized day-period labels; only shown when {@link hour12} is true. */
  readonly dayPeriods: { am: string; pm: string };
}

/** The four selectable minute slots (15-minute steps). */
export const MINUTE_STEPS: readonly number[] = [0, 15, 30, 45] as const;

/** Hour-column values for a 24-hour clock. */
export const HOURS_24: readonly number[] = Array.from({ length: 24 }, (_, h) => h);

/** Hour-column values for a 12-hour clock (12 leads, then 1…11). */
export const HOURS_12: readonly number[] = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

/** A known Sunday at UTC midnight, used to enumerate localized weekday names. */
const REFERENCE_SUNDAY_UTC = Date.UTC(2023, 0, 1);

/** First day of week (JS numbering) for a locale — from `Intl` week info when
 * available, with a Sunday-first fallback for US/Canada and Monday elsewhere. */
function resolveFirstDayOfWeek(locale: string): WeekdayIndex {
  try {
    const loc = new Intl.Locale(locale) as Intl.Locale & {
      weekInfo?: { firstDay?: number };
      getWeekInfo?: () => { firstDay?: number };
    };
    const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
    const firstDay = info?.firstDay;
    // `Intl` week info numbers days 1 = Monday … 7 = Sunday; `% 7` maps that to
    // JS numbering (7 → 0 = Sunday, 1 → 1 = Monday, …).
    if (typeof firstDay === 'number') return (firstDay % 7) as WeekdayIndex;
  } catch {
    // Unsupported locale string or no week-info support — fall through.
  }
  const region = locale.toLowerCase().split('-')[1];
  return region === 'us' || region === 'ca' ? 0 : 1;
}

/** Localized AM/PM labels, read off a morning and an evening time. */
function resolveDayPeriods(locale: string): { am: string; pm: string } {
  const fmt = new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true });
  const read = (hour: number, fallback: string) =>
    fmt.formatToParts(new Date(2023, 0, 1, hour)).find((p) => p.type === 'dayPeriod')?.value ??
    fallback;
  return { am: read(9, 'AM'), pm: read(21, 'PM') };
}

/** Build the resolved {@link DateTimeFormat} for an active UI language. */
export function createDateTimeFormat(uiLang: string): DateTimeFormat {
  const locale = resolveLocale(uiLang);
  const hour12 =
    new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hour12 ?? false;
  const firstDayOfWeek = resolveFirstDayOfWeek(locale);

  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    weekdayFmt.format(new Date(REFERENCE_SUNDAY_UTC + ((firstDayOfWeek + i) % 7) * 86400000))
  );

  return { locale, hour12, firstDayOfWeek, weekdayLabels, dayPeriods: resolveDayPeriods(locale) };
}

/** Trigger display string for a value — locale date order + locale clock. */
export function formatDisplay(ms: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(ms)
  );
}

/** "September 2026"-style month/year heading for the calendar. */
export function formatMonthYear(year: number, month: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(year, month, 1)
  );
}

// ── Time-model helpers ──────────────────────────────────────────────────────
// The picker's source of truth is `hour24` (0–23) + `minute`. The 12-hour view
// derives a displayed hour (12, 1…11) plus a day period from it.

export interface Hour12 {
  /** Displayed 12-hour number (12, 1…11). */
  hour: number;
  /** True for PM. */
  pm: boolean;
}

export function to12h(hour24: number): Hour12 {
  const pm = hour24 >= 12;
  const h = hour24 % 12;
  return { hour: h === 0 ? 12 : h, pm };
}

export function from12h(hour12: number, pm: boolean): number {
  const base = hour12 % 12; // 12 → 0, 1…11 unchanged
  return pm ? base + 12 : base;
}

/** Zero-padded two-digit label (e.g. `7` → `"07"`). */
export function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

// ── Date-part helpers ───────────────────────────────────────────────────────

export interface DateParts {
  year: number;
  month: number; // 0-based
  day: number;
  hour: number; // 0–23
  minute: number;
}

export function decompose(ms: number): DateParts {
  const d = new Date(ms);
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
    hour: d.getHours(),
    minute: d.getMinutes(),
  };
}

export function partsToMs(p: DateParts): number {
  return new Date(p.year, p.month, p.day, p.hour, p.minute, 0, 0).getTime();
}

/** `nowMs` rounded *up* to the next 00/15/30/45 slot (kept as-is when already
 * on one). Seconds are dropped. Rolls into the next hour/day as needed. */
export function nextQuarterHour(nowMs: number): number {
  const d = new Date(nowMs);
  // Seconds past the minute mean the slot-aligned minute is already in the
  // past — bump to the next minute before aligning, so 9:00:40 rounds up to
  // 9:15 rather than back to 9:00.
  if (d.getSeconds() > 0 || d.getMilliseconds() > 0) {
    d.setSeconds(0, 0);
    d.setMinutes(d.getMinutes() + 1);
  }
  const add = (15 - (d.getMinutes() % 15)) % 15;
  d.setMinutes(d.getMinutes() + add);
  return d.getTime();
}

/** `ms` rounded *down* to the previous 00/15/30/45 slot (kept when already on
 * one). Mirror of {@link nextQuarterHour} for clamping against an upper bound. */
export function floorQuarterHour(ms: number): number {
  const d = new Date(ms);
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - (d.getMinutes() % 15));
  return d.getTime();
}

/** Clamp an instant into `[min, max]` (either bound optional). */
export function clampMs(ms: number, min: number | null, max: number | null): number {
  let out = ms;
  if (min != null && out < min) out = min;
  if (max != null && out > max) out = max;
  return out;
}

// ── Calendar grid ───────────────────────────────────────────────────────────

export interface DayCell {
  year: number;
  month: number; // 0-based
  day: number;
  /** False for leading/trailing days that belong to the adjacent month. */
  inCurrentMonth: boolean;
}

/**
 * A fixed 6×7 (42-cell) month grid starting on `firstDayOfWeek`, including the
 * leading/trailing days of the neighbouring months so every row is full.
 */
export function buildMonthGrid(
  year: number,
  month: number,
  firstDayOfWeek: WeekdayIndex
): DayCell[] {
  const firstOfMonth = new Date(year, month, 1);
  // How many leading days from the previous month to show.
  const lead = (firstOfMonth.getDay() - firstDayOfWeek + 7) % 7;
  const start = new Date(year, month, 1 - lead);

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      day: d.getDate(),
      inCurrentMonth: d.getMonth() === month,
    };
  });
}

// ── Constraint helpers (vs. min/max epoch ms) ───────────────────────────────

/** A whole day is disabled when even its last slot (23:45) precedes `min`, or
 * its first slot (00:00) is after `max`. */
export function isDayDisabled(
  year: number,
  month: number,
  day: number,
  min: number | null,
  max: number | null
): boolean {
  if (min != null && partsToMs({ year, month, day, hour: 23, minute: 45 }) < min) return true;
  if (max != null && partsToMs({ year, month, day, hour: 0, minute: 0 }) > max) return true;
  return false;
}

/** An hour is disabled when no minute slot within it lands inside `[min, max]`. */
export function isHourDisabled(
  year: number,
  month: number,
  day: number,
  hour: number,
  min: number | null,
  max: number | null
): boolean {
  return !MINUTE_STEPS.some(
    (minute) => !isMinuteDisabled(year, month, day, hour, minute, min, max)
  );
}

/** A specific hour:minute on a day is disabled when it falls outside `[min, max]`. */
export function isMinuteDisabled(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  min: number | null,
  max: number | null
): boolean {
  const ms = partsToMs({ year, month, day, hour, minute });
  if (min != null && ms < min) return true;
  if (max != null && ms > max) return true;
  return false;
}
